/**
 * @author <ziad.khoury.hanna@oceandatalab.com>
 * @flow
 */
import Events from 'minivents';
import superagent from 'superagent';

export const hub /*: Events */ = new Events();
export const statistics = {
  issuedRequestCount: 0,
  liveRequestCount: 0,
  completedRequestCount: 0,
  erroredRequestCount: 0,
  canceledRequestCount: 0,
};

/*:: type AjaxOptions = {
  method: string,
  cache?: boolean,
  data?: Object | string,
  dataType?: string,
  responseType?: string,
  headers?: {[header: string]: string},
  withCredentials?: boolean,
  timeout?: number,
  username?: string,
  password?: string,
  progress?: (newText: string) => void,
  signal?: ?AbortSignal,
}; */
/*:: type AjaxProgressContext = {
  numberOfCharsAlreadyGivenToCallback: number,
  callback?: (newText: string) => void,
}; */

function handleProgress(event /*: ProgressEvent */) /*: void */ {
  const xhr /*: XMLHttpRequest */ = (event.target /*: any */);

  if (xhr.status !== 200 || xhr.responseType !== 'text' || !xhr.responseText) {
    return;
  }

  const textSoFar = xhr.responseText;
  const newText = textSoFar.substr(this.numberOfCharsAlreadyGivenToCallback);

  /* Raise the event for new text.

    On older browsers, the new text is the whole response.
    On newer/better ones, the fragment part that we got since
    last progress. */

  if (this.callback && newText) {
    this.callback(newText);
  }

  this.numberOfCharsAlreadyGivenToCallback = textSoFar.length;
}

async function superagentAjax(
  url /*: string */,
  options /*: AjaxOptions */,
  progressContext /*: AjaxProgressContext */
) /*: Promise<any> */ {
  const request = superagent(options.method, url);

  if (!options.cache) {
    request.query({_: `${Math.floor(Math.random() * 1e4)}-${Date.now()}`});
  }
  if (
    options.data
    && (options.method.toUpperCase() === 'GET'
      || options.method.toUpperCase() === 'HEAD')
  ) {
    request.query(options.data);
    request.send();
  } else if (options.data) {
    request.send(options.data);
  }
  if (options.dataType) {
    request.type(options.dataType);
  }
  if (options.responseType) {
    request.responseType(options.responseType);
  }
  if (options.headers) {
    request.set(options.headers);
  }
  if (options.withCredentials) {
    request.withCredentials();
  }
  if (options.timeout) {
    request.timeout({response: options.timeout});
  }
  if (options.username && options.password) {
    request.auth(options.username, options.password);
  }
  if (options.signal) {
    options.signal.addEventListener('abort', () => request.abort());
  }

  request.on('progress', handleProgress.bind(progressContext));

  const response = await request;
  if (response?.xhr.responseType === 'text') {
    let event;

    try {
      event = new ProgressEvent('progress');
    } catch (err) {
      event = document.createEvent('Event');
      event.initEvent('progress', true, true);
    }
    response.xhr.dispatchEvent(event);
  }
  return response;
}

async function _ajax(
  url /*: string */,
  options /*: AjaxOptions */,
  remainingRetries /*: number */
) /*: Promise<any> */ {
  statistics.issuedRequestCount++;
  statistics.liveRequestCount++;
  hub.emit('pre-send');

  const progressContext = {
    numberOfCharsAlreadyGivenToCallback: 0,
    callback: options.progress,
  };
  try {
    const response = await superagentAjax(url, options, progressContext);

    statistics.liveRequestCount--;
    statistics.completedRequestCount++;
    hub.emit('success');

    return response;
  } catch (error) {
    if (remainingRetries > 0) {
      let shouldRetry = false;

      if (error.name === 'AbortError') {
        /* Don't retry aborted requests */
      } else if (error instanceof TypeError) {
        /* Network error */
        // shouldRetry = shouldRetry || error instanceof TypeError;
      } else {
        /* HTTP error */
        const status = error.response.status;

        shouldRetry ||=
          status === 408 // Request Timeout
          || status === 503 // Service Unavailable
          || status === 504 // Gateway Timeout
          || status === 522 // Connection timed out
          || status === 524; // A timeout occurred
      }

      if (shouldRetry) {
        await new Promise((r) => setTimeout(r, 500));
        return _ajax(url, options, remainingRetries - 1);
      }
    }

    statistics.liveRequestCount--;
    if (error.name === 'AbortError') {
      statistics.canceledRequestCount++;
      hub.emit('cancel');
    } else {
      statistics.erroredRequestCount++;
      hub.emit('fail');
    }

    throw error;
  }
}

export function ajax(
  url /*: string */,
  options /*:: ?: ?$Shape<AjaxOptions> */
) /*: Promise<any> */ {
  options = {
    method: 'GET',
    responseType: 'text',
    ...options,
  };

  return _ajax(url, options, 3);
}
